import {engineContext} from './context.js';

(function(global){
  'use strict';

  const VERSION='0.1.0-alpha';
  const ORDER=['us','europe','asia','em','china','india','latam'];

  function finite(v){ return typeof v==='number' && Number.isFinite(v); }
  function clamp(v,a,b){ return Math.max(a,Math.min(b,v)); }

  function normalizeRegion(x){
    x=x||{};
    const key=String(x.key||'').toLowerCase();
    if(!ORDER.includes(key)) return null;
    const trend=Array.isArray(x.trend)?x.trend.filter(finite).slice(-24):[];
    return Object.freeze({
      key,
      return30d:finite(x.return30d)?x.return30d:null,
      trend,
      verification:['VERIFIED','PARTIAL','UNVERIFIED'].includes(x.verification)?x.verification:'UNVERIFIED'
    });
  }

  function normalizeSnapshot(input){
    input=input||{};
    const rows=Array.isArray(input.regions)?input.regions.map(normalizeRegion).filter(Boolean):[];
    const byKey=Object.fromEntries(rows.map(r=>[r.key,r]));
    return Object.freeze({
      regions:ORDER.map(k=>byKey[k]||normalizeRegion({key:k})),
      asOf:input.asOf||null,
      mode:input.mode==='LIVE'?'LIVE':'DEMO'
    });
  }

  function coverage(snapshot){
    const s=normalizeSnapshot(snapshot);
    const present=s.regions.filter(r=>finite(r.return30d));
    const verified=present.filter(r=>r.verification==='VERIFIED');
    return Object.freeze({
      present:present.length,
      total:s.regions.length,
      ratio:present.length/s.regions.length,
      verifiedRatio:present.length?verified.length/present.length:0
    });
  }

  function breadth(snapshot){
    const s=normalizeSnapshot(snapshot);
    const vals=s.regions.filter(r=>finite(r.return30d));
    if(!vals.length) return Object.freeze({state:'MISSING',positive:null,negative:null,flat:null,ratioPositive:null});
    const positive=vals.filter(r=>r.return30d>0).length;
    const negative=vals.filter(r=>r.return30d<0).length;
    const flat=vals.length-positive-negative;
    const ratioPositive=positive/vals.length;
    const state = ratioPositive>=0.71 ? 'BROAD_POSITIVE'
                : ratioPositive<=0.29 ? 'BROAD_NEGATIVE'
                : 'MIXED';
    return Object.freeze({state,positive,negative,flat,ratioPositive});
  }

  function dispersion(snapshot){
    const s=normalizeSnapshot(snapshot);
    const vals=s.regions.map(r=>r.return30d).filter(finite);
    if(vals.length<2) return Object.freeze({state:'MISSING',range:null,stdDev:null});
    const max=Math.max(...vals), min=Math.min(...vals);
    const mean=vals.reduce((a,b)=>a+b,0)/vals.length;
    const variance=vals.reduce((a,b)=>a+(b-mean)**2,0)/vals.length;
    const stdDev=Math.sqrt(variance);
    const range=max-min;
    const state = range>=12 || stdDev>=5 ? 'HIGH'
                : range>=6 || stdDev>=2.5 ? 'MODERATE'
                : 'LOW';
    return Object.freeze({state,range,stdDev});
  }

  function leadership(snapshot){
    const s=normalizeSnapshot(snapshot);
    const vals=s.regions.filter(r=>finite(r.return30d));
    if(!vals.length) return Object.freeze({leaders:[],laggards:[],spread:null});
    const sorted=[...vals].sort((a,b)=>b.return30d-a.return30d);
    return Object.freeze({
      leaders:Object.freeze(sorted.slice(0,2).map(r=>Object.freeze({key:r.key,return30d:r.return30d}))),
      laggards:Object.freeze(sorted.slice(-2).reverse().map(r=>Object.freeze({key:r.key,return30d:r.return30d}))),
      spread:sorted[0].return30d-sorted[sorted.length-1].return30d
    });
  }

  function trendDirection(series){
    if(!Array.isArray(series) || series.length<4) return 'UNKNOWN';
    const first=series.slice(0,Math.ceil(series.length/2));
    const last=series.slice(Math.floor(series.length/2));
    const avg=a=>a.reduce((x,y)=>x+y,0)/a.length;
    const delta=avg(last)-avg(first);
    if(delta>0.75) return 'IMPROVING';
    if(delta<-0.75) return 'DETERIORATING';
    return 'STABLE';
  }

  function rotation(snapshot){
    const s=normalizeSnapshot(snapshot);
    const rows=s.regions
      .filter(r=>finite(r.return30d))
      .map(r=>({key:r.key,return30d:r.return30d,direction:trendDirection(r.trend),verification:r.verification}));

    const improving=rows.filter(r=>r.direction==='IMPROVING').map(r=>r.key);
    const deteriorating=rows.filter(r=>r.direction==='DETERIORATING').map(r=>r.key);

    let state='INSUFFICIENT';
    if(rows.filter(r=>r.direction!=='UNKNOWN').length>=3){
      if(improving.length>=3 && deteriorating.length===0) state='BROADENING';
      else if(deteriorating.length>=3 && improving.length===0) state='NARROWING';
      else state='ROTATING';
    }
    return Object.freeze({state,improving:Object.freeze(improving),deteriorating:Object.freeze(deteriorating)});
  }

  function context(snapshot){
    const s=normalizeSnapshot(snapshot);
    const cov=coverage(s);
    const br=breadth(s);
    const disp=dispersion(s);
    const lead=leadership(s);
    const rot=rotation(s);

    let status='REVIEW';
    if(cov.ratio<0.60) status='INSUFFICIENT_DATA';
    else if(br.state==='BROAD_POSITIVE' && disp.state!=='HIGH') status='CONSTRUCTIVE';
    else if(br.state==='BROAD_NEGATIVE') status='RISK_ELEVATED';
    else if(disp.state==='HIGH') status='ROTATION_RISK';

    return Object.freeze({
      status,
      coverage:cov,
      breadth:br,
      dispersion:disp,
      leadership:lead,
      rotation:rot,
      caveat:'Context only. Does not alter Company Quality and is not a trade signal.'
    });
  }

  function uiSummary(snapshot){
    const c=context(snapshot);
    return Object.freeze({
      status:c.status,
      line1:c.breadth.state==='MISSING'
        ? 'Regional breadth unavailable'
        : `${c.breadth.positive}/${c.breadth.positive+c.breadth.negative+c.breadth.flat} regions positive`,
      line2:c.dispersion.state==='MISSING'
        ? 'Dispersion unavailable'
        : `${c.dispersion.state} regional dispersion`,
      leader:c.leadership.leaders[0]||null,
      laggard:c.leadership.laggards[0]||null,
      rotation:c.rotation.state,
      verificationCoverage:c.coverage.verifiedRatio
    });
  }

  function selfTest(){
    const tests=[];
    const ok=(name,pass)=>tests.push({name,pass:!!pass});

    const pos={regions:ORDER.map((key,i)=>({key,return30d:2+i*.4,trend:[0,0.2,0.8,1.4],verification:'VERIFIED'}))};
    const neg={regions:ORDER.map((key,i)=>({key,return30d:-2-i*.5,trend:[1,.5,0,-1],verification:'VERIFIED'}))};
    const mixed={regions:[
      {key:'us',return30d:10,trend:[0,1,2,3],verification:'VERIFIED'},
      {key:'europe',return30d:1,trend:[0,0,0,0],verification:'VERIFIED'},
      {key:'asia',return30d:-8,trend:[1,0,-1,-2],verification:'VERIFIED'},
      {key:'em',return30d:3,trend:[0,1,1,2],verification:'PARTIAL'}
    ]};

    ok('positive breadth detected',breadth(pos).state==='BROAD_POSITIVE');
    ok('negative breadth detected',breadth(neg).state==='BROAD_NEGATIVE');
    ok('positive context constructive',context(pos).status==='CONSTRUCTIVE');
    ok('negative context risk elevated',context(neg).status==='RISK_ELEVATED');
    ok('high dispersion detected',dispersion(mixed).state==='HIGH');
    ok('leadership preserves strongest region',leadership(mixed).leaders[0].key==='us');
    ok('laggard preserves weakest region',leadership(mixed).laggards[0].key==='asia');
    ok('missing data stays missing',breadth({regions:[]}).state==='MISSING');
    ok('insufficient coverage blocks strong conclusion',context(mixed).status==='INSUFFICIENT_DATA');
    ok('trend improving detected',trendDirection([0,0.2,1.1,1.8])==='IMPROVING');
    ok('trend deteriorating detected',trendDirection([2,1.5,0.4,-0.5])==='DETERIORATING');
    ok('verification coverage is explicit',coverage(mixed).verifiedRatio===0.75);

    return Object.freeze({passed:tests.filter(t=>t.pass).length,total:tests.length,tests:Object.freeze(tests)});
  }

  const API={
    VERSION,ORDER,normalizeRegion,normalizeSnapshot,coverage,breadth,dispersion,
    leadership,trendDirection,rotation,context,uiSummary,selfTest
  };
  try{ API.selfTestResult=selfTest(); }catch(e){ API.bootstrapError=String(e&&e.stack||e); }
  global.ZooInvestGlobalMarketContextEngine=Object.freeze(API);
})(engineContext);
